import { useUser } from 'hooks'
import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  clearLocalDraft,
  clearLocalDrafts,
  listLocalDraftKeys,
  loadLocalDraft,
  saveLocalDraft,
} from 'utils/drafts/sessionDraft'
import { IItemDraftHandle, IModalDraft } from 'types/hocTypes'
import { isWizardStep, WizardStep } from './wizardSteps'

/** Drafts are kept for two weeks unless the wizard is finished/skipped or the user logs out. */
const WIZARD_DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000

const LAST_STEP = 'lastStep'

/** Window event sent after a wizard draft is saved or removed in this tab. */
const DRAFTS_CHANGED_EVENT = 'cv-wizard-drafts-changed'

/**
 * Tells listeners (the stepper's draft marks) that wizard drafts changed. Sent asynchronously, because drafts can
 * change while a card renders (a draft of a deleted item is removed when the card mounts).
 */
const notifyDraftsChanged = () => {
  queueMicrotask(() => window.dispatchEvent(new Event(DRAFTS_CHANGED_EVENT)))
}

/**
 * Key prefix of all wizard drafts of one user and CV.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @returns Key prefix.
 */
export const buildWizardDraftPrefix = (userId: number, cvId: number) => `draft:cv-wizard:${userId}:${cvId}:`

/**
 * Saves unsaved input of a wizard step.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 * @param payload - Draft content.
 */
export const saveWizardStepDraft = <T>(userId: number, cvId: number, step: WizardStep, payload: T) => {
  saveLocalDraft(buildWizardDraftPrefix(userId, cvId) + step, payload, WIZARD_DRAFT_TTL_MS)
  notifyDraftsChanged()
}

/**
 * Loads the draft of a wizard step.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 * @returns Draft content or null.
 */
export const loadWizardStepDraft = <T>(userId: number, cvId: number, step: WizardStep) =>
  loadLocalDraft<T>(buildWizardDraftPrefix(userId, cvId) + step)

/**
 * Removes the draft of a wizard step, e.g. after the step has been saved.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 */
export const clearWizardStepDraft = (userId: number, cvId: number, step: WizardStep) => {
  clearLocalDraft(buildWizardDraftPrefix(userId, cvId) + step)
  notifyDraftsChanged()
}

/**
 * Remembers the step the user was last on, so the wizard can be continued there.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 */
export const saveWizardLastStep = (userId: number, cvId: number, step: WizardStep) =>
  saveLocalDraft(buildWizardDraftPrefix(userId, cvId) + LAST_STEP, step, WIZARD_DRAFT_TTL_MS)

/**
 * Returns the step the user was last on.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @returns Last step or undefined.
 */
export const loadWizardLastStep = (userId: number, cvId: number): WizardStep | undefined => {
  const step = loadLocalDraft<string>(buildWizardDraftPrefix(userId, cvId) + LAST_STEP) ?? undefined
  return isWizardStep(step) ? step : undefined
}

/**
 * Returns the steps that have an unsaved draft.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @returns Steps with a draft, in no particular order.
 */
export const getWizardDraftSteps = (userId: number, cvId: number): WizardStep[] => {
  const prefix = buildWizardDraftPrefix(userId, cvId)
  // Step drafts are `<prefix><step>`, dialog drafts `<prefix><step>:item:<itemKey>`.
  const steps = listLocalDraftKeys(prefix)
    .map(key => key.slice(prefix.length).split(':')[0])
    .filter(isWizardStep)
  return [...new Set(steps)]
}

/**
 * Removes all wizard drafts of the CV, including the last step. Used on finish, skip and discard.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 */
export const clearWizardDrafts = (userId: number, cvId: number) => {
  clearLocalDrafts(buildWizardDraftPrefix(userId, cvId))
  notifyDraftsChanged()
}

/**
 * Steps of the CV that have an unsaved draft, for the logged in user. Follows drafts saved and removed while the
 * wizard is open, also in other tabs.
 *
 * @param cvId - ID of the CV.
 * @returns Steps with a draft, in no particular order.
 */
export const useWizardDraftSteps = (cvId: number | undefined) => {
  const { user } = useUser()
  const userId = user?.id
  const [draftSteps, setDraftSteps] = useState<WizardStep[]>([])

  useEffect(() => {
    const update = () => {
      const steps = userId && cvId ? getWizardDraftSteps(userId, cvId).sort() : []
      // Dialog drafts are saved on every change; only re-render when the set of steps changes.
      setDraftSteps(previous => (isEqual(previous, steps) ? previous : steps))
    }

    update()
    window.addEventListener(DRAFTS_CHANGED_EVENT, update)
    window.addEventListener('storage', update)
    return () => {
      window.removeEventListener(DRAFTS_CHANGED_EVENT, update)
      window.removeEventListener('storage', update)
    }
  }, [userId, cvId])

  return draftSteps
}

/**
 * Draft of one wizard step for the logged in user.
 *
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 * @returns `initialDraft` (the draft stored when the step opened), `saveDraft` and `clearDraft`.
 */
export const useWizardStepDraft = <T>(cvId: number, step: WizardStep) => {
  const { user } = useUser()
  const userId = user?.id

  const initialDraft = useMemo(() => (userId ? loadWizardStepDraft<T>(userId, cvId, step) : null), [userId, cvId, step])

  const saveDraft = useCallback(
    (payload: T) => {
      if (userId) saveWizardStepDraft(userId, cvId, step, payload)
    },
    [userId, cvId, step]
  )

  const clearDraft = useCallback(() => {
    if (userId) clearWizardStepDraft(userId, cvId, step)
  }, [userId, cvId, step])

  return { initialDraft, saveDraft, clearDraft }
}

/** A dialog draft as stored: the form data plus the paths of the values that were `Date`s. */
interface IStoredItemDraft {
  data: unknown
  datePaths: string[][]
}

/**
 * Collects the paths of all `Date` values, so they can be turned back into `Date`s after JSON storage.
 *
 * @param value - Value to walk.
 * @param path - Path of the value.
 * @param paths - Collected paths.
 * @returns The collected paths.
 */
const collectDatePaths = (value: unknown, path: string[] = [], paths: string[][] = []): string[][] => {
  if (value instanceof Date) {
    paths.push(path)
  } else if (value && typeof value === 'object') {
    for (const [key, child] of Object.entries(value)) {
      collectDatePaths(child, [...path, key], paths)
    }
  }
  return paths
}

/**
 * Turns the values at the given paths back into `Date`s.
 *
 * @param data - Parsed draft data (changed in place).
 * @param datePaths - Paths of the `Date` values.
 * @returns The data.
 */
const reviveDates = (data: unknown, datePaths: string[][]) => {
  for (const path of datePaths) {
    let parent = data as Record<string, unknown> | undefined
    for (const key of path.slice(0, -1)) {
      parent = parent?.[key] as Record<string, unknown> | undefined
    }
    const last = path[path.length - 1]
    if (parent && last !== undefined && typeof parent[last] === 'string') {
      parent[last] = new Date(parent[last] as string)
    }
  }
  return data
}

/**
 * Key prefix of the dialog drafts of one step.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 * @returns Key prefix.
 */
const buildItemDraftPrefix = (userId: number, cvId: number, step: WizardStep) =>
  `${buildWizardDraftPrefix(userId, cvId)}${step}:item:`

/**
 * Creates the dialog drafts of a profile card on a wizard list step (the card's `itemDraft` prop).
 * Only one dialog can be open at a time, so a step keeps at most one dialog draft: saving one removes the others.
 * Drafts are keyed `<section>-<item id>` (`<section>-new` for a new item), so items of different sections
 * (e.g. courses and certificates on the education step) don't mix.
 *
 * @param userId - ID of the logged in account.
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 * @param sections - The card's items per section; the first section is used for new items.
 * @param sectionOf - Tells which section an existing item belongs to (default: the first section).
 * @returns Dialog draft handle.
 */
export const createWizardItemDraft = (
  userId: number,
  cvId: number,
  step: WizardStep,
  sections: Record<string, { id?: number }[]>,
  sectionOf?: (item: { id?: number }) => string | undefined
): IItemDraftHandle => {
  const prefix = buildItemDraftPrefix(userId, cvId, step)
  const [defaultSection] = Object.keys(sections)
  const keyFor = (item: { id?: number } | null) =>
    `${prefix}${(item && sectionOf?.(item)) ?? defaultSection}-${item?.id ?? 'new'}`

  const forItem = (item: { id?: number } | null): IModalDraft => {
    const key = keyFor(item)
    return {
      load: () => {
        const stored = loadLocalDraft<IStoredItemDraft>(key)
        return stored ? reviveDates(stored.data, stored.datePaths) : null
      },
      save: data => {
        for (const other of listLocalDraftKeys(prefix)) {
          if (other !== key) clearLocalDraft(other)
        }
        saveLocalDraft<IStoredItemDraft>(key, { data, datePaths: collectDatePaths(data) }, WIZARD_DRAFT_TTL_MS)
        notifyDraftsChanged()
      },
      clear: () => {
        clearLocalDraft(key)
        notifyDraftsChanged()
      },
    }
  }

  const getPendingItem = () => {
    for (const key of listLocalDraftKeys(prefix)) {
      const itemKey = key.slice(prefix.length)
      const separator = itemKey.lastIndexOf('-')
      const section = itemKey.slice(0, separator)
      const id = itemKey.slice(separator + 1)
      if (!(section in sections)) continue
      if (id === 'new') return null
      const item = sections[section].find(sectionItem => String(sectionItem.id) === id)
      if (item) return item
      // The item was deleted meanwhile; its draft can't be continued.
      clearLocalDraft(key)
      notifyDraftsChanged()
    }
    return undefined
  }

  return { getPendingItem, forItem }
}

/**
 * Dialog drafts of the cards on a wizard list step, for the logged in user.
 *
 * @param cvId - ID of the CV.
 * @param step - Wizard step.
 * @returns A function that creates a card's `itemDraft` handle (see `createWizardItemDraft`), or `undefined`
 * without a user.
 */
export const useWizardItemDrafts = (cvId: number, step: WizardStep) => {
  const { user } = useUser()
  const userId = user?.id

  return useCallback(
    (sections: Record<string, { id?: number }[]>, sectionOf?: (item: { id?: number }) => string | undefined) =>
      userId ? createWizardItemDraft(userId, cvId, step, sections, sectionOf) : undefined,
    [userId, cvId, step]
  )
}
